import { useEffect, useState } from "react";
import { Phone, Mail, MapPin, Instagram, Facebook, Send, CheckCircle2, AlertCircle, Calendar } from "lucide-react";
import { fetchPages } from "../lib/cms";
import { virksomhedsinfo } from "../data";

export default function Kontakt() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Formular tilstande
  const [formData, setFormData] = useState({
    navn: "",
    telefonnummer: "",
    email: "",
    beskrivelse: "",
    website_url: "", // Honeypot felt
  });
  const [formStatus, setFormStatus] = useState({
    submitting: false,
    success: false,
    error: null,
  });

  useEffect(() => {
    async function loadData() {
      const pages = await fetchPages();
      const kontaktPage = pages.find((p) => p.page_slug === "kontakt");
      setPageData(kontaktPage);
      setLoading(false);
    }
    loadData();
  }, []);

  // Sæt sidetitel og meta-beskrivelse
  useEffect(() => {
    if (pageData) {
      document.title = pageData.meta_title || "Kontakt - RG Fodpleje";
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute("content", pageData.meta_description || "Kontakt RG Fodpleje i Odense N.");
      }
    }
  }, [pageData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus({ submitting: true, success: false, error: null });

    // Enkel validering
    if (!formData.navn || !formData.telefonnummer || !formData.email || !formData.beskrivelse) {
      setFormStatus({
        submitting: false,
        success: false,
        error: "Udfyld venligst alle påkrævede felter.",
      });
      return;
    }

    try {
      const response = await fetch("https://api.dev.auxo.dk/webform/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.navn,
          phone: formData.telefonnummer,
          email: formData.email,
          message: formData.beskrivelse,
          website_url: formData.website_url, // Honeypot
        }),
      });

      if (!response.ok) {
        throw new Error("Der skete en fejl ved afsendelse. Prøv igen senere.");
      }

      setFormStatus({
        submitting: false,
        success: true,
        error: null,
      });

      // Nulstil formular
      setFormData({
        navn: "",
        telefonnummer: "",
        email: "",
        beskrivelse: "",
        website_url: "",
      });
    } catch (err) {
      setFormStatus({
        submitting: false,
        success: false,
        error: err.message || "Kunne ikke oprette forbindelse til serveren.",
      });
    }
  };

  return (
    <div className="bg-[#FAF8F5] text-[#2D2A26] min-h-screen py-12 sm:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Overskrift */}
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <span className="text-xs font-semibold tracking-widest uppercase text-[#4A5D4E] bg-[#E8EFEA] px-3 py-1.5 rounded-full">
            Kontakt Rikke
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl font-bold text-[#2D2A26]">
            Kontakt mig
          </h1>
          <p className="text-[#6B6661] font-light leading-relaxed max-w-xl mx-auto">
            Har du spørgsmål, eller ønsker du at bestille en tid? Du er altid velkommen til at kontakte mig.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          {/* Venstre kolonne: Kontaktinfo & Rutevejledning */}
          <div className="lg:col-span-5 space-y-8">
            
            {/* Kontaktkort */}
            <div className="bg-white rounded-2xl border border-[#EAE5DF] shadow-sm p-8 space-y-6">
              <h2 className="font-serif text-2xl font-semibold text-[#2D2A26]">
                RG Fodpleje
              </h2>
              
              {/* Præcis kontakttekst fra scrape */}
              {pageData && (
                <div 
                  className="text-[#6B6661] font-light leading-relaxed border-b border-[#FAF8F5] pb-4"
                  dangerouslySetInnerHTML={{ __html: pageData.content }}
                />
              )}

              <div className="space-y-4 text-sm">
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-full bg-[#E8EFEA] text-[#4A5D4E] flex items-center justify-center shrink-0">
                    <MapPin className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-medium text-[#2D2A26]">Adresse</h4>
                    <p className="text-[#6B6661] font-light mt-0.5">{virksomhedsinfo.adresse}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-full bg-[#E8EFEA] text-[#4A5D4E] flex items-center justify-center shrink-0">
                    <Phone className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-medium text-[#2D2A26]">Telefon</h4>
                    <a
                      href={`tel:+45${virksomhedsinfo.telefon.replace(/\s+/g, "")}`}
                      className="text-[#4A5D4E] hover:underline font-medium block mt-0.5 whitespace-nowrap"
                    >
                      {virksomhedsinfo.telefon}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-full bg-[#E8EFEA] text-[#4A5D4E] flex items-center justify-center shrink-0">
                    <Mail className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-medium text-[#2D2A26]">E-mail</h4>
                    <a
                      href={`mailto:${virksomhedsinfo.email}`}
                      className="text-[#4A5D4E] hover:underline font-medium block mt-0.5 whitespace-nowrap"
                    >
                      {virksomhedsinfo.email}
                    </a>
                  </div>
                </div>
              </div>

              {/* Sociale medier */}
              <div className="pt-6 border-t border-[#EAE5DF] space-y-3">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-[#6B6661]">
                  Følg os på sociale medier
                </h4>
                <div className="flex gap-4">
                  <a
                    href={virksomhedsinfo.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-[#6B6661] hover:text-[#4A5D4E] transition-colors"
                  >
                    <Facebook className="h-5 w-5 text-[#4A5D4E]" />
                    <span>Facebook</span>
                  </a>
                  <a
                    href={virksomhedsinfo.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-[#6B6661] hover:text-[#4A5D4E] transition-colors"
                  >
                    <Instagram className="h-5 w-5 text-[#4A5D4E]" />
                    <span>Instagram</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Rutevejledning & Kort */}
            <div className="bg-[#F3EFEA] rounded-2xl border border-[#EAE5DF] p-8 space-y-6">
              <h3 className="font-serif text-xl font-semibold text-[#2D2A26]">
                Sådan finder du os
              </h3>
              <p className="text-[#6B6661] text-sm font-light leading-relaxed">
                Klinikken er beliggende i rolige omgivelser på Søhusvænget 25 i Odense N. Der er gode parkeringsmuligheder lige ved døren.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <a
                  href="https://maps.google.com/?cid=13228531197329572076"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white hover:bg-[#FAF8F5] text-[#2D2A26] border border-[#EAE5DF] px-5 py-3 rounded-full text-xs font-medium tracking-wide text-center shadow-sm transition-colors flex-1"
                >
                  Åbn i Google Maps
                </a>
                <a
                  href="https://www.google.com/maps/dir//''/data=!4m7!4m6!1m1!4e2!1m2!1m1!1s0x464ce0421af2445f:0xb7952f09450080ec!3e0"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#4A5D4E] hover:bg-[#3B4C3E] text-[#FAF8F5] px-5 py-3 rounded-full text-xs font-medium tracking-wide text-center shadow-sm transition-colors flex-1"
                >
                  Få rutevejledning
                </a>
              </div>
            </div>

          </div>

          {/* Højre kolonne: Kontaktformular */}
          <div className="lg:col-span-7">
            <div className="bg-white rounded-2xl border border-[#EAE5DF] shadow-sm p-8 sm:p-10 space-y-6">
              <div className="space-y-2">
                <h2 className="font-serif text-2xl font-semibold text-[#2D2A26]">
                  Send en besked
                </h2>
                <p className="text-sm text-[#6B6661] font-light">
                  Udfyld formularen nedenfor, så vender jeg tilbage til dig hurtigst muligt.
                </p>
              </div>

              {formStatus.success ? (
                <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 p-6 rounded-xl space-y-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-6 w-6 text-emerald-600 shrink-0" />
                    <h3 className="font-semibold text-base">Beskeden er sendt!</h3>
                  </div>
                  <p className="text-sm text-emerald-700 font-light leading-relaxed">
                    Mange tak for din henvendelse. Jeg har modtaget din besked og vil vende tilbage til dig så hurtigt som muligt på e-mail eller telefon.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  
                  {/* Honeypot (Antispam) - Absolut skjult for mennesker */}
                  <div className="sr-only absolute -top-[9999px] -left-[9999px]">
                    <label htmlFor="website_url">Efterlad dette felt tomt</label>
                    <input
                      type="text"
                      id="website_url"
                      name="website_url"
                      value={formData.website_url}
                      onChange={handleChange}
                      tabIndex="-1"
                      autoComplete="off"
                    />
                  </div>

                  {/* Navn */}
                  <div className="space-y-1.5">
                    <label htmlFor="navn" className="text-xs font-semibold uppercase tracking-wider text-[#6B6661]">
                      Navn <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="navn"
                      name="navn"
                      required
                      value={formData.navn}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-[#EAE5DF] bg-[#FAF8F5] text-[#2D2A26] focus:outline-none focus:border-[#4A5D4E] focus:bg-white transition-colors text-sm"
                      placeholder="Dit fulde navn"
                    />
                  </div>

                  {/* Telefonnummer */}
                  <div className="space-y-1.5">
                    <label htmlFor="telefonnummer" className="text-xs font-semibold uppercase tracking-wider text-[#6B6661]">
                      Telefonnummer <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="telefonnummer"
                      name="telefonnummer"
                      required
                      value={formData.telefonnummer}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-[#EAE5DF] bg-[#FAF8F5] text-[#2D2A26] focus:outline-none focus:border-[#4A5D4E] focus:bg-white transition-colors text-sm"
                      placeholder="Dit telefonnummer"
                    />
                  </div>

                  {/* Email */}
                  <div className="space-y-1.5">
                    <label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-[#6B6661]">
                      E-mailadresse <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-[#EAE5DF] bg-[#FAF8F5] text-[#2D2A26] focus:outline-none focus:border-[#4A5D4E] focus:bg-white transition-colors text-sm"
                      placeholder="din@email.dk"
                    />
                  </div>

                  {/* Beskrivelse */}
                  <div className="space-y-1.5">
                    <label htmlFor="beskrivelse" className="text-xs font-semibold uppercase tracking-wider text-[#6B6661]">
                      Beskrivelse <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="beskrivelse"
                      name="beskrivelse"
                      required
                      rows="5"
                      value={formData.beskrivelse}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-[#EAE5DF] bg-[#FAF8F5] text-[#2D2A26] focus:outline-none focus:border-[#4A5D4E] focus:bg-white transition-colors text-sm resize-none"
                      placeholder="Skriv din besked eller forespørgsel her..."
                    />
                  </div>

                  {/* Fejlmeddelelse */}
                  {formStatus.error && (
                    <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-xl flex items-center gap-2 text-sm">
                      <AlertCircle className="h-5 w-5 text-red-600 shrink-0" />
                      <span>{formStatus.error}</span>
                    </div>
                  )}

                  {/* Submit knap */}
                  <button
                    type="submit"
                    disabled={formStatus.submitting}
                    className="w-full bg-[#4A5D4E] hover:bg-[#3B4C3E] disabled:bg-stone-400 text-[#FAF8F5] py-4 rounded-full text-sm font-semibold tracking-wide transition-colors shadow-md hover:shadow-lg flex items-center justify-center gap-2 focus:outline-none"
                  >
                    {formStatus.submitting ? (
                      <span>Sender besked...</span>
                    ) : (
                      <>
                        <Send className="h-4 w-4" />
                        <span>Send besked</span>
                      </>
                    )}
                  </button>

                </form>
              )}
            </div>
          </div>

        </div>

        {/* Hurtig Booking CTA */}
        <div className="mt-20 bg-[#E8EFEA] rounded-3xl p-8 sm:p-12 border border-[#C2D1C5] text-center space-y-6 max-w-4xl mx-auto">
          <h3 className="font-serif text-2xl sm:text-3xl font-bold text-[#2E4233]">
            Vil du hellere booke en tid med det samme?
          </h3>
          <p className="text-[#4A5D4E] text-base max-w-xl mx-auto font-light">
            Slip for ventetid og find en tid, der passer dig perfekt i vores online bookingsystem. Det tager kun 2 minutter.
          </p>
          <div>
            <a
              href="https://rg-fodpleje.planway.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#4A5D4E] hover:bg-[#3B4C3E] text-[#FAF8F5] px-8 py-4 rounded-full text-base font-semibold tracking-wide shadow-md hover:shadow-lg transition-colors"
            >
              <Calendar className="h-5 w-5" />
              <span>Gå til online booking</span>
            </a>
          </div>
        </div>

      </div>
    </div>
  );
}
